/**
 * Detects if a logo image is primarily white/light or dark/grey/black on a transparent background.
 * This helps determine if a background color should be applied for better visibility.
 * 
 * @param imageElement - The loaded HTMLImageElement to analyze
 * @returns Object with:
 *   - isWhite: true if logo is primarily white/light colors
 *   - isDark: true if logo is primarily dark/grey/black colors
 *   - needsBackground: true if logo needs a background for visibility
 *   - averageBrightness: average brightness value (0-255)
 */
export function detectLogoColor(imageElement: HTMLImageElement): {
  isWhite: boolean;
  isDark: boolean;
  needsBackground: boolean;
  averageBrightness: number;
} {
  if (!imageElement || !imageElement.complete || imageElement.naturalWidth === 0) {
    // Image not loaded yet or invalid
    return {
      isWhite: false,
      isDark: false,
      needsBackground: false,
      averageBrightness: 128, // Default middle value
    };
  }

  try {
    // Create a canvas to analyze the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      return {
        isWhite: false,
        isDark: false,
        needsBackground: false,
        averageBrightness: 128,
      };
    }

    const width = imageElement.naturalWidth;
    const height = imageElement.naturalHeight;
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw the image to the canvas
    ctx.drawImage(imageElement, 0, 0);
    
    // Get image data - this can fail with CORS errors on cross-origin images
    let imageData: ImageData;
    try {
      imageData = ctx.getImageData(0, 0, width, height);
    } catch (error) {
      // CORS error - image is tainted and can't be read
      // This is expected when loading production images on localhost
      return {
        isWhite: false,
        isDark: false,
        needsBackground: false,
        averageBrightness: 128,
      };
    }
    const data = imageData.data;
    
    // Analyze pixels
    let totalBrightness = 0;
    let pixelCount = 0;
    let whitePixelCount = 0;
    let darkPixelCount = 0;
    
    // Sample pixels (every Nth pixel for performance)
    const sampleRate = Math.max(1, Math.floor((width * height) / 1000)); // Sample up to 1000 pixels
    
    for (let i = 0; i < data.length; i += 4 * sampleRate) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const alpha = data[i + 3];
      
      // Only analyze non-transparent pixels
      if (alpha < 128) {
        continue; // Skip transparent or semi-transparent pixels
      }
      
      // Calculate brightness using luminance formula
      const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
      
      totalBrightness += brightness;
      pixelCount++;
      
      // Classify as white (brightness > 200) or dark (brightness < 80)
      if (brightness > 200) {
        whitePixelCount++;
      } else if (brightness < 80) {
        darkPixelCount++;
      }
    }
    
    if (pixelCount === 0) {
      // All pixels are transparent
      return {
        isWhite: false,
        isDark: false,
        needsBackground: false,
        averageBrightness: 128,
      };
    }
    
    const averageBrightness = totalBrightness / pixelCount;
    const whiteRatio = whitePixelCount / pixelCount;
    const darkRatio = darkPixelCount / pixelCount;
    
    // Determine if logo is primarily white or dark
    // Threshold: if >60% of pixels are white, consider it a white logo
    // If >60% of pixels are dark, consider it a dark logo
    const isWhite = whiteRatio > 0.6;
    const isDark = darkRatio > 0.6;
    
    // Logo needs background if it's primarily white or dark (not mixed/colored)
    const needsBackground = isWhite || isDark;
    
    return {
      isWhite,
      isDark,
      needsBackground,
      averageBrightness,
    };
  } catch (error) {
    // If analysis fails (e.g., CORS error on localhost with production images), return safe defaults
    // Only log non-CORS errors to avoid console spam
    if (error instanceof Error && !error.message.includes('tainted') && !error.message.includes('CORS')) {
      console.warn('Failed to detect logo color:', error);
    }
    return {
      isWhite: false,
      isDark: false,
      needsBackground: false,
      averageBrightness: 128,
    };
  }
}
